"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/logo";
import type { Translations, Locale } from "@/lib/translations";

const PHONES: Record<Locale, { display: string; href: string }> = {
  en: { display: "+1 415 449 5591", href: "tel:+14154495591" },
  lt: { display: "+370 647 36 245", href: "tel:+37064736245" },
};

function LangSwitcher({ locale, light }: { locale: Locale; light?: boolean }) {
  const pathname = usePathname();

  function getAlternateUrl() {
    if (locale === "en") {
      return `/lt${pathname === "/" ? "" : pathname}`;
    } else {
      return pathname.replace(/^\/lt/, "") || "/";
    }
  }

  const altLocale = locale === "en" ? "lt" : "en";
  const altUrl = getAlternateUrl();

  return (
    <Link
      href={altUrl}
      aria-label={`Switch to ${altLocale.toUpperCase()}`}
      className={
        light
          ? "font-label-caps text-[10px] uppercase tracking-widest font-bold text-white/60 hover:text-white transition-colors duration-200 border border-white/20 px-2 py-0.5 hover:border-white/50"
          : "font-label-caps text-[11px] uppercase tracking-widest font-bold text-on-surface-variant hover:text-primary transition-colors duration-200 border border-black/20 px-2.5 py-1 hover:border-primary"
      }
    >
      {altLocale.toUpperCase()}
    </Link>
  );
}

export function Nav({ locale = "en", t }: { locale?: Locale; t?: Translations["nav"] }) {
  const services = t?.services ?? "Services";
  const caseStudies = t?.caseStudies ?? "Case Studies";
  const pricing = t?.pricing ?? "Pricing";
  const blog = t?.blog ?? "Blog";
  const cta = t?.cta ?? "Free Automation Scope";

  const prefix = locale === "lt" ? "/lt" : "";

  const links = [
    { href: `${prefix}/services`,     label: services     },
    { href: `${prefix}/case-studies`, label: caseStudies  },
    { href: `${prefix}/#pricing`,     label: pricing      },
    { href: `${prefix}/blog`,         label: blog         },
  ];

  const [open, setOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const [navHeight, setNavHeight] = useState(0);
  const close = () => setOpen(false);

  useEffect(() => {
    if (navRef.current) {
      setNavHeight(navRef.current.offsetHeight);
    }
  }, []);

  const phone = PHONES[locale];

  return (
    <>
      {/* Top info bar — desktop only, scrolls away */}
      <div className="hidden md:block w-full bg-black">
        <div className="max-w-[1440px] mx-auto px-10 py-1.5 flex items-center justify-between">
          <LangSwitcher locale={locale} light />
          <div className="flex items-center gap-6">
            <a
              href="mailto:hello@fluxrivet.com"
              className="inline-flex items-center gap-1.5 font-label-caps text-[10px] uppercase tracking-widest text-white/60 hover:text-white transition-colors duration-200"
            >
              <Mail size={9} strokeWidth={2} />
              hello@fluxrivet.com
            </a>
            <a
              href={phone.href}
              className="inline-flex items-center gap-1.5 font-label-caps text-[10px] uppercase tracking-widest text-white/60 hover:text-white transition-colors duration-200"
            >
              <Phone size={9} strokeWidth={2} />
              {phone.display}
            </a>
          </div>
        </div>
      </div>

      <nav ref={navRef} className="sticky top-0 z-50 w-full border-b border-black">
        {/* Main nav */}
        <div className="w-full bg-white/90 backdrop-blur-sm">
          <div className="max-w-[1440px] mx-auto w-full flex items-center px-4 md:px-10 py-4">
            <div className="flex-1">
              <Link href={prefix || "/"} aria-label="Fluxrivet home">
                <Logo />
              </Link>
            </div>

            <ul className="hidden md:flex gap-8 list-none m-0 p-0">
              {links.map((l) => (
                <li key={l.href}>
                  <Link
                    href={l.href}
                    className="font-label-caps text-[12px] text-on-surface-variant uppercase tracking-widest font-bold hover:text-primary transition-colors duration-200"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="flex-1 flex justify-end items-center">
              <Button className="hidden md:inline-flex" asChild>
                <Link href={`${prefix}/contact`}>{cta}</Link>
              </Button>
            </div>

            {/* Hamburger */}
            <button
              className="md:hidden flex flex-col gap-[5px] p-2"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle navigation"
              aria-expanded={open}
            >
              <span
                className="w-6 h-px bg-primary block transition-all duration-200"
                style={{ transform: open ? "translateY(6px) rotate(45deg)" : undefined }}
              />
              <span
                className="w-6 h-px bg-primary block transition-all duration-200"
                style={{ opacity: open ? 0 : 1 }}
              />
              <span
                className="w-6 h-px bg-primary block transition-all duration-200"
                style={{ transform: open ? "translateY(-6px) rotate(-45deg)" : undefined }}
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div
          className="fixed inset-x-0 z-40 bg-white border-b border-black flex flex-col md:hidden"
          style={{ top: navHeight }}
          role="navigation"
        >
          {links.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              onClick={close}
              className="font-label-caps text-[12px] uppercase tracking-widest font-bold px-10 py-5 border-b border-black hover:bg-surface-container-low transition-colors"
            >
              {l.label}
            </Link>
          ))}
          <div className="px-10 py-6 flex flex-col gap-6">
            <Button className="w-full py-3" asChild>
              <Link href={`${prefix}/contact`} onClick={close}>{cta}</Link>
            </Button>
            <div className="flex items-center justify-between border-t border-black/10 pt-4">
              <div className="flex flex-col gap-3">
                <a
                  href="mailto:hello@fluxrivet.com"
                  className="inline-flex items-center gap-2 font-label-caps text-[11px] uppercase tracking-widest text-on-surface-variant hover:text-primary transition-colors"
                >
                  <Mail size={12} strokeWidth={2} />
                  hello@fluxrivet.com
                </a>
                <a
                  href={phone.href}
                  className="inline-flex items-center gap-2 font-label-caps text-[11px] uppercase tracking-widest text-on-surface-variant hover:text-primary transition-colors"
                >
                  <Phone size={12} strokeWidth={2} />
                  {phone.display}
                </a>
              </div>
              <LangSwitcher locale={locale} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
